import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe } from 'i18n';

import { FunnelService } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'WolfPack Funnel';
const MIN_PASSWORD_LENGTH = 8;

/**
 * Final step of the WolfPack Distribution "Private Application" wizard. The
 * application is already saved + the operator notified (on quiz completion);
 * here the soft-logged-in user sets their password inline (no purchase) and is
 * dropped into the app.
 */
@Component({
  selector: 'app-funnel-result-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-result-page.component.html',
  styleUrls: ['./funnel-result-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelResultPageComponent {
  private router     = inject(Router);
  private funnel     = inject(FunnelService);
  private i18n       = inject(I18nService);
  private matomo     = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);

  loading = signal(true);
  error   = signal<string | null>(null);

  password = signal('');
  confirm  = signal('');
  busy     = signal(false);
  pwError  = signal<string | null>(null);

  minLength = MIN_PASSWORD_LENGTH;

  canSubmit = computed(() =>
    this.password().length >= MIN_PASSWORD_LENGTH &&
    this.password() === this.confirm() &&
    !this.busy()
  );

  constructor() {
    if (!this.funnel.getSessionToken()) {
      this.redirectToLanding();
      return;
    }

    this.funnel.showQuiz()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => {
          this.loading.set(false);
          if (session.status !== 'completed' && session.status !== 'purchased') {
            // Not finished yet — back to the wizard.
            const lang = this.i18n.getLanguage() || 'en';
            this.router.navigate(['/', lang, 'quiz'], { replaceUrl: true });
          }
        },
        error: err => {
          this.loading.set(false);
          if (err?.status === 401 || err?.status === 404) {
            this.funnel.clearSessionToken();
            this.redirectToLanding();
            return;
          }
          this.error.set(err?.error?.error || 'Could not load your application.');
        }
      });
  }

  submitPassword(): void {
    if (!this.canSubmit()) {
      if (this.password() !== this.confirm()) {
        this.pwError.set('mismatch');
      }
      return;
    }

    this.busy.set(true);
    this.pwError.set(null);

    this.funnel.setPassword(this.password())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'password_set' });
          this.funnel.clearSessionToken();
          // Soft-login `cid` cookie keeps the user authenticated → straight into the app.
          const lang = this.i18n.getLanguage() || 'en';
          this.router.navigate(['/', lang], { replaceUrl: true });
        },
        error: err => {
          this.busy.set(false);
          this.pwError.set(err?.error?.error || 'failed');
        }
      });
  }

  private redirectToLanding(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'r'], { replaceUrl: true });
  }
}
